/**
 * CEX Trading widget
 */
import { useEffect, useReducer } from 'react';
import { useSelector } from 'react-redux';
import { useAccount } from 'wagmi';
import BigNumber from 'bignumber.js';
import { Network } from '@kattana-io/common';
import Text from '../../../../../atoms/Text';
import TradingBox from '../../../../../molecules/TradingBox';
import NonEditableParamField from '../../../common/NonEditableParamField';
import Button from '../../../../../molecules/Button';
import { IStore } from '../../../../../reducers';
import { authTokenSelector } from '../../../../../selectors/auth';
import useAppState from '../../../hooks/useAppSwate';
import {
  CexOrderType,
  CexTradingAction,
  ICexCoin,
  cexTradingReducer,
  getInitialCexTradingState,
} from '../../../reducers/cex/cexTradingReducer';
import { actionGetOrders } from '../../../../../actions/cex_orders';
import { ECEXState } from '../../../../../reducers/cex_trading';
import { actionShowModal } from '../../../../../actions/modal';
import { ModalTypes } from '../../../../../reducers/modal';
import useTokenBalance from '../../../../../hooks/useTokenBalance';
import useTickerLivePrice from '../../../../Watchlist/hooks/useTickerLivePrice';
import { CexAPI } from '../../../../../store/createStore';
import { supportedCEXTradingExchanges } from '../../..';
import { capitalize } from '../../../../Modal/TickersModal/helpers';

import '../../../styles.scss';

enum OrderSide {
  UNDEFINED_SIDE = 0,
  BUY = 1,
  SELL = 2,
}

const network = Network.createETH();

interface ICexSwapProps {
  exchange: string;
  height: string | number;
}

const createdOrderMessage = (
  orderType: CexOrderType,
  side: OrderSide,
  base: ICexCoin,
  quote: ICexCoin,
  price: number
) => {
  const sideF = side === OrderSide.BUY ? 'buy' : 'sell';
  switch (orderType) {
  case CexOrderType.LIMIT:
    return `Created ${sideF} limit order ${price} ${base.name}/${quote.name}`;
  case CexOrderType.MARKET:
    return `Created ${sideF} market order ${base.name}/${quote.name} for ${quote.name} ${quote.amount}`;
  default:
    return '';
  }
};

const CexSwap = ({ exchange, height }: ICexSwapProps) => {
  const authToken = useSelector(authTokenSelector);
  const exchangeState = useSelector(
    (store: IStore) => store.cex_trading.exchanges[exchange]
  );
  const cexConnectedState = useSelector(
    (state: IStore) => state.cex_trading.state
  );
  const { address } = useAccount();

  // state
  const { current, onNotify, globalDispatch } = useAppState();
  const [tradingState, dispatch] = useReducer(
    cexTradingReducer,
    current,
    getInitialCexTradingState
  );

  useEffect(() => {
    const { base, quote, price } = getInitialCexTradingState(current);
    dispatch({ type: CexTradingAction.SetBaseCoin, payload: base });
    dispatch({ type: CexTradingAction.SetQuoteCoin, payload: quote });
    dispatch({ type: CexTradingAction.SetPrice, payload: price });
  }, [current]);

  const baseBalance = useTokenBalance({
    address,
    token: current.base,
    exchange,
    chainId: network.toDecimal(),
    enabled: address && supportedCEXTradingExchanges.includes(exchange),
    interval: 10000,
  });
  const quoteBalance = useTokenBalance({
    address,
    token: current.quote,
    exchange,
    chainId: network.toDecimal(),
    enabled: address && supportedCEXTradingExchanges.includes(exchange),
    interval: 10000,
  });
  const marketPrice = useTickerLivePrice(current);

  // Functions
  const onBaseUpdate = (input: number) => {
    dispatch({
      type: CexTradingAction.SetBaseAmount,
      payload: new BigNumber(input),
    });
    dispatch({
      type: CexTradingAction.SetQuoteAmount,
      payload: new BigNumber(input).multipliedBy(tradingState.price),
    });
  };

  const onQuoteUpdate = (input: number) => {
    dispatch({
      type: CexTradingAction.SetQuoteAmount,
      payload: new BigNumber(input),
    });
    dispatch({
      type: CexTradingAction.SetBaseAmount,
      payload: new BigNumber(input).dividedBy(tradingState.price),
    });
  };

  const onSetTab = (payload: CexOrderType) =>
    dispatch({ type: CexTradingAction.SetOrderType, payload });

  /**
   * Connect API key
   * @param side
   */
  const onConnect = (_: any) => {
    if (address) {
      return globalDispatch(
        actionShowModal(ModalTypes.connectCex, { exchange })
      );
    }

    globalDispatch(actionShowModal(ModalTypes.connect, {}));
  };

  /**
   * Update current price from last market price
   */
  const onSetCurrentFromMarketPrice = () =>
    dispatch({
      type: CexTradingAction.SetPrice,
      payload: BigNumber(marketPrice.price.rawPrice),
    });
  /**
   * Lock and send order
   * @param side
   */
  const onAction = async (side: OrderSide) => {
    const loadingAction =
      side === OrderSide.BUY
        ? CexTradingAction.ToggleBuyLoading
        : CexTradingAction.ToggleSellLoading;

    if (tradingState.base.amount.isZero()) {
      onNotify({
        kind: 'warning',
        message: 'Could not create order with zero amount',
      });
      return;
    }

    if (
      tradingState.orderType === CexOrderType.LIMIT &&
      tradingState.price.isZero()
    ) {
      onNotify({
        kind: 'warning',
        message: 'Could not create limit order with 0 price',
      });
      return;
    }

    try {
      dispatch({ type: loadingAction });
      const order = await CexAPI.createOrder(
        authToken!,
        exchange,
        `${tradingState.base.name}/${tradingState.quote.name}`,
        tradingState.orderType,
        side,
        tradingState.price.toNumber(),
        tradingState.base.amount.toNumber(),
        tradingState.quote.amount.toNumber()
      );
      console.log(order);
      if (order.status) {
        onNotify({
          kind: 'info',
          message: createdOrderMessage(
            tradingState.orderType,
            side,
            tradingState.base,
            tradingState.quote,
            tradingState.price.toNumber()
          ),
        });
        globalDispatch(actionGetOrders());
      } else {
        onNotify({
          kind: 'warning',
          message: order.message
            ? capitalize(order.message)
            : 'Could not create order',
        });
      }
    } catch (err: any) {
      onNotify({
        kind: 'warning',
        message: 'Could not create order',
      });
      console.error(err);
    } finally {
      dispatch({ type: loadingAction });
    }
  };

  const renderActions = () => {
    if (!supportedCEXTradingExchanges.includes(exchange)) {
      return (
        <Button
          size='l'
          action={undefined}
          variant='secondary'
          disabled={true}
          text={`${exchange} trading coming soon`}
          loading={cexConnectedState === ECEXState.loading}
        />
      );
    }

    if (!exchangeState?.enable || !address) {
      return (
        <Button
          size={address ? 'l' : 'm'}
          action={onConnect}
          disabled={cexConnectedState === ECEXState.loading}
          variant='primary'
          text={
            address ? 'Enter API Key' : 'Connect wallet to use this feature'
          }
          loading={cexConnectedState === ECEXState.loading}
        />
      );
    }
    return (
      <>
        <Button
          size='l'
          action={() => onAction(OrderSide.BUY)}
          disabled={tradingState.buyLoading}
          variant='buy'
          text={`Buy ${tradingState.base.name}`}
          loading={tradingState.buyLoading}
        />
        <Button
          size='l'
          action={() => onAction(OrderSide.SELL)}
          disabled={tradingState.sellLoading}
          variant='sell'
          text={`Sell ${tradingState.base.name}`}
          loading={tradingState.sellLoading}
        />
      </>
    );
  };

  const fee = tradingState.base.amount.multipliedBy(new BigNumber(0.02));

  return (
    <div className='trading' style={{ flexBasis: height }}>
      <div className='container'>
        <div className='cex_swap__inputs'>
          {tradingState.orderType == CexOrderType.MARKET && (
            <TradingBoxPlaceholder
              coinTitle='Price'
              placeholder='market'
              className='cex_swap__price_field'
            />
          )}
          {tradingState.orderType == CexOrderType.LIMIT && (
            <TradingBox
              disabled={false}
              coinTitle='Price'
              placeholder='limit'
              value={tradingState.price}
              onChange={(input) =>
                dispatch({
                  type: CexTradingAction.SetPrice,
                  payload: new BigNumber(input),
                })
              }
              isCoinChangable={false}
              className='cex_swap__price_field'
            />
          )}
          <TradingBox
            network={network}
            disabled={false}
            title='Send'
            balance={baseBalance.toNumber()}
            coinAddress={tradingState.base.address}
            coinTitle={tradingState.base.name}
            value={tradingState.base.amount}
            onChange={onBaseUpdate}
            isCoinChangable={false}
          />
          <TradingBox
            network={network}
            disabled={false}
            title='Receive'
            balance={quoteBalance.toNumber()}
            coinAddress={tradingState.quote.address}
            coinTitle={tradingState.quote.name}
            value={tradingState.quote.amount}
            onChange={onQuoteUpdate}
            isCoinChangable={false}
          />
        </div>
        <div className='tab_selector'>
          <div
            className={`tab ${
              tradingState.orderType == CexOrderType.MARKET ? 'tab--active' : ''
            }`}
            onClick={() => onSetTab(CexOrderType.MARKET)}
          >
            <Text size={14}>Market</Text>
          </div>
          <div
            className={`tab ${
              tradingState.orderType == CexOrderType.LIMIT ? 'tab--active' : ''
            }`}
            onClick={() => onSetTab(CexOrderType.LIMIT)}
          >
            <Text size={12}>Limit</Text>
          </div>
        </div>
        <div className='params_panel'>
          <NonEditableParamField
            title='Price'
            onClick={onSetCurrentFromMarketPrice}
            text={`${marketPrice.price.rawPrice.toFixed(4)} ${
              tradingState.quote.name
            } per ${tradingState.base.name}`}
          />
          <NonEditableParamField
            title='Fee'
            text={`${fee.toFixed(4)} ${tradingState.base.name}`}
          />
        </div>
        <div className='cex_swap__buttons'>{renderActions()}</div>
      </div>
    </div>
  );
};

/**
 * Tradebox-like placeholder
 * @param props
 * @returns
 */
const TradingBoxPlaceholder = (props: any) => (
  <div className={`trading_box ${props.className}`} aria-label={props.title}>
    <div className='row input_row'>
      <div className='coin_row' aria-label='Change coin' onClick={props.setTab}>
        <Text
          className='coin_row__title'
          size={18}
          style={{ title: props.coinTitle }}
        >
          {props.coinTitle}
        </Text>
      </div>
      <div className='input'>{props.placeholder}</div>
    </div>
  </div>
);

export default CexSwap;
